import {
  type ColumnsType,
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  findUpperRecursive,
  getRecursiveArray,
  getRecursiveIds,
} from '@/utils';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateRevenueNature,
  useDeleteRevenueNature,
  useRevenueNatureRecursive,
  useRevenueNatures,
  useUpdateRevenueNature,
} from '../../hooks';
import {
  AccountingTypeLabelModifier,
  type RevenueNatureBaseModel,
} from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToAccountType,
  fillZeroToRevenueNature,
  getBudgetAgendaSortOrderDataSource,
} from '../../utils';
import { BudgetAgendaSortOrderModal } from '../BudgetAgendaSortOrderModal';
import { useDrawerState } from '../DrawerStateProvider';
import { RevenueNatureFormDrawer } from './RevenueNatureFormDrawer';

// The defaultExpandAllRows should be set false to reduce the number of rows to be rendered when the page is loaded.
const DEFAULT_EXPAND_ALL_ROWS = false;

export const RevenueNaturesPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const revenueNatures = useRevenueNatures();

  const isOpeningRevenueNatureFormDrawer =
    drawerState.open && drawerState.type === 'revenue-nature';

  const editingRevenueNature = revenueNatures.data?.find(
    (d) =>
      isOpeningRevenueNatureFormDrawer &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );
  const injectingRevenueNature = revenueNatures.data?.find(
    (d) =>
      isOpeningRevenueNatureFormDrawer &&
      !drawerState.isEditing &&
      drawerState.creatingLevel === d.level + 1 &&
      drawerState.creatingUpperId === d.id,
  );

  const revenueNaturesRecursive = useRevenueNatureRecursive(
    editingRevenueNature?.upperId ?? injectingRevenueNature?.id ?? '',
  );

  const { mutateAsync: createMutateAsync } = useCreateRevenueNature();
  const { mutateAsync: updateMutateAsync } = useUpdateRevenueNature();
  const { mutateAsync: deleteMutateAsync } = useDeleteRevenueNature();
  const editPermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();

  const [budgetAgendaSortOrderModalState, setBudgetAgendaSortOrderModalState] =
    useState<{
      open: boolean;
      level: number | null;
      accountingId: string | null;
      upperId: string | null;
    }>({
      open: false,
      level: null,
      accountingId: null,
      upperId: null,
    });

  const [revenueNatureDelete, setRevenueNatureDelete] = useState<z.infer<
    typeof RevenueNatureBaseModel
  > | null>(null);

  if (revenueNatures.isPending || !revenueNatures.data) {
    return <Loading />;
  }

  if (revenueNatures.isError) {
    openMessage('error', t('failed-fetch-revenue-natures'));
  }

  const groupedRevenueNatures = Object.entries(
    groupBy(
      getRecursiveArray(
        revenueNatures.data.filter((d) => !d.upperId),
        revenueNatures.data,
      ),
      'accountingId',
    ),
  )
    .flatMap(([accountingId, data]) => {
      const accounting = data.find(
        (d) => d.accountingId === accountingId,
      )?.accounting;
      if (!accounting) {
        return [];
      }
      return {
        id: accountingId,
        code: accounting.code,
        type: accounting.type,
        name: accounting.name,
        data,
      };
    })
    .sort((a, b) => a.code - b.code);

  const openAllExpanded = () => {
    setExpandedRowKeys(
      groupedRevenueNatures.reduce<string[]>((acc, cur) => {
        if (cur.data.length === 0) {
          return acc;
        }
        acc.push(cur.id);
        acc.push(...getRecursiveIds(cur.data));
        return acc;
      }, []),
    );
  };

  const handleCloseDrawer = async () => {
    setDrawerState({ open: false });
  };

  const LevelLabelModifier = {
    1: t('revenue-nature-level-1'),
    2: t('revenue-nature-level-2'),
    3: t('revenue-nature-level-3'),
    4: t('revenue-nature-level-4'),
    5: t('revenue-nature-level-5'),
  };

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={groupedRevenueNatures}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToAccountType,
          },
          {
            title: t('accounting-type'),
            key: 'type',
            dataIndex: 'type',
            width: 150,
            render: (value) => AccountingTypeLabelModifier[value],
          },
          {
            title: t('accounting-name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          defaultExpandAllRows: DEFAULT_EXPAND_ALL_ROWS,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          rowExpandable: (record) => record.data.length > 0,
          expandedRowRender: ({ data }) => {
            const pageSize = 5;

            return (
              <Table
                enableSticky={false}
                style={{ marginLeft: 80 }}
                dataSource={data}
                rowKey="id"
                pagination={
                  data.length > pageSize
                    ? {
                        pageSize,
                      }
                    : undefined
                }
                expandable={{
                  defaultExpandAllRows: DEFAULT_EXPAND_ALL_ROWS,
                  expandedRowKeys,
                  onExpand: (expanded, record) => {
                    onExpand(expanded, record.id);
                  },
                  rowExpandable: (record) =>
                    !!(record.children && record.children.length === 0),
                }}
                columns={[
                  {
                    title: t('code'),
                    dataIndex: 'code',
                    width: 180,
                    render: (value: number, record) => (
                      <Text
                        weight="regular"
                        text={fillZeroToRevenueNature(
                          value,
                          record.level === 4,
                        )}
                      />
                    ),
                  },
                  {
                    title: t('level'),
                    dataIndex: 'level',
                    width: 100,
                    render: (value) => (
                      <Text weight="regular" text={LevelLabelModifier[value]} />
                    ),
                  },
                  {
                    title: t('name'),
                    dataIndex: 'name',
                    width: 240,
                    render: (value) => <Text weight="regular" text={value} />,
                  },
                  {
                    title: t('start-fiscal-year'),
                    dataIndex: 'startYear',
                    width: TABLE_YEAR_WIDTH,
                    render: (value) => <Text weight="regular" text={value} />,
                  },
                  {
                    title: t('end-fiscal-year'),
                    dataIndex: 'endYear',
                    width: TABLE_YEAR_WIDTH,
                    render: (value) => <Text weight="regular" text={value} />,
                  },
                  {
                    title: t('created-datetime'),
                    dataIndex: 'createdAt',
                    width: TABLE_DATETIME_WIDTH,
                    render: (datetime) => (
                      <Text
                        weight="regular"
                        text={datetimeFormat({ datetime })}
                      />
                    ),
                  },
                  {
                    title: t('updated-datetime'),
                    dataIndex: 'updatedAt',
                    width: TABLE_DATETIME_WIDTH,
                    render: (datetime) => (
                      <Text
                        weight="regular"
                        text={datetimeFormat({ datetime })}
                      />
                    ),
                  },
                  ...(editPermission.data
                    ? ([
                        {
                          title: '',
                          key: 'actions',
                          fixed: 'right',
                          width: TABLE_ROW_MENU_WIDTH,
                          onCell: () => ({
                            style: { minWidth: TABLE_ROW_MENU_WIDTH },
                          }),
                          render: (_, record) => (
                            <TableRowMenuCell
                              onEdit={() => {
                                setDrawerState({
                                  open: true,
                                  type: 'revenue-nature',
                                  isEditing: true,
                                  id: record.id,
                                });
                              }}
                              items={[
                                ...(record.level > 0 && record.level < 5
                                  ? [
                                      {
                                        key: 'createNextLevelRevenueNature',
                                        label: t('create-item', {
                                          item: LevelLabelModifier[
                                            record.level + 1
                                          ],
                                        }),
                                        onClick: () => {
                                          setDrawerState({
                                            open: true,
                                            type: 'revenue-nature',
                                            isEditing: false,
                                            creatingLevel: (record.level +
                                              1) as any,
                                            creatingUpperId: record.id,
                                          });
                                        },
                                      },
                                    ]
                                  : []),
                                {
                                  key: 'budget-agenda-sort-order',
                                  label: t('budget-agenda-sort-order-edit'),
                                  onClick: () => {
                                    return setBudgetAgendaSortOrderModalState({
                                      open: true,
                                      level: record.level,
                                      accountingId: record.accountingId ?? null,
                                      upperId: record.upperId,
                                    });
                                  },
                                },
                                {
                                  key: 'delete',
                                  label: t('delete'),
                                  danger: true,
                                  onClick: () => setRevenueNatureDelete(record),
                                },
                              ]}
                            />
                          ),
                        },
                      ] satisfies ColumnsType<(typeof data)[number]>)
                    : []),
                ]}
              />
            );
          },
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <RevenueNatureFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...editingRevenueNature,
                period: [
                  editingRevenueNature?.startYear
                    ? dayjs().year(editingRevenueNature.startYear)
                    : undefined,
                  editingRevenueNature?.endYear
                    ? dayjs().year(editingRevenueNature.endYear)
                    : undefined,
                ],
                upperIds: revenueNaturesRecursive.data?.map((d) => d.id),
              },
            }
          : {
              type: 'new',
              initialValues:
                isOpeningRevenueNatureFormDrawer && !drawerState.isEditing
                  ? {
                      level: drawerState.creatingLevel,
                      upperIds: revenueNaturesRecursive.data?.map((d) => d.id),
                    }
                  : undefined,
            })}
        onSubmit={async (values) => {
          if (!drawerState.open) {
            return null;
          }

          const res = drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            handleCloseDrawer(),
            !drawerState.isEditing &&
              (async () => {
                if (res.upperId) {
                  if (!revenueNatures.data) {
                    return;
                  }
                  const uppers = findUpperRecursive(
                    res.upperId,
                    revenueNatures.data,
                  );
                  addExpandedRowKeys([res.id, ...uppers.map((d) => d.id)]);
                }
              })(),
          ]);
        }}
      />
      <ConfirmModal
        open={revenueNatureDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(revenueNatureDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(revenueNatureDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setRevenueNatureDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setRevenueNatureDelete(null)}
        skipCheck
      />

      <BudgetAgendaSortOrderModal
        type="revenue-nature"
        open={budgetAgendaSortOrderModalState.open}
        dataSource={getBudgetAgendaSortOrderDataSource({
          data: revenueNatures.data,
          budgetAgendaSortOrderModalState: {
            level: budgetAgendaSortOrderModalState.level,
            accountingId: budgetAgendaSortOrderModalState.accountingId,
            upperId: budgetAgendaSortOrderModalState.upperId,
          },
        })}
        onCancel={() =>
          setBudgetAgendaSortOrderModalState({
            open: false,
            level: null,
            accountingId: null,
            upperId: null,
          })
        }
      />
    </>
  );
};
